import React from 'react';
import { Link } from 'react-router-dom';
import SeoPageLayout, { useSeo, seoTextStyles as s } from '../../components/seo/SeoPageLayout';

export default function HowToMakeASpinningRecordVideo() {
  useSeo(
    'How to Make a Spinning Record Video (Step by Step) — Waxify',
    'A step-by-step guide to turning album art and an audio track into a spinning-disc video for Instagram, TikTok, or YouTube using Waxify.'
  );

  return (
    <SeoPageLayout>
      <h1 className={s.h1}>How to Make a Spinning Record Video</h1>
      <p className={s.lead}>
        Here's the full process, start to finish, for turning a piece of art and an
        audio file into a spinning-disc video you can post today.
      </p>

      <h2 className={s.h2}>1. Prepare your two images</h2>
      <p className={s.p}>
        You need a piece of disc art — usually your cover art, cropped square — and,
        optionally, a background image that sits behind the spinning disc. The disc
        art is what actually rotates, so a centered, symmetrical design (like a real
        record label) tends to look best once it's spinning.
      </p>

      <h2 className={s.h2}>2. Upload your disc art</h2>
      <p className={s.p}>
        Open Waxify and drop your image into the "Disc" panel. You can mask it into a
        circle, a rounded square, or a few other shapes, or switch to "Vinyl" mode to
        wrap it as a proper record label sitting on black vinyl with visible grooves
        and a spindle hole.
      </p>

      <h2 className={s.h2}>3. Set the speed and direction</h2>
      <p className={s.p}>
        The Speed slider runs from a slow crawl up to 300 RPM, with one-click presets
        for the classic turntable speeds — 16⅔, 33⅓, 45, and 78 RPM. If you want the
        disc to spin counter-clockwise instead, flip the "Reverse spin" switch; the
        number itself always stays positive so the RPM reading matches what you'd set
        on a real player.
      </p>

      <h2 className={s.h2}>4. Add a background (optional)</h2>
      <p className={s.p}>
        Drop in a photo or pick a solid color for whatever sits behind the disc. This
        is what fills the rest of the frame once you choose a vertical or square
        export size, so it matters more for portrait exports than square ones.
      </p>

      <h2 className={s.h2}>5. Upload and trim your audio</h2>
      <p className={s.p}>
        Add your track in the "Audio" panel. Drag the trim handles on the waveform to
        pick the exact section you want, and use the zoom controls to get frame-accurate
        with the start and end points — handy for teasing a chorus or hook rather than
        an intro. Fade-in and fade-out length are adjustable too.
      </p>

      <h2 className={s.h2}>6. Choose your export size</h2>
      <p className={s.p}>
        Pick a preset sized for where you're posting — square works everywhere,
        portrait (9:16) is right for Reels, TikTok, and Stories, and landscape suits
        YouTube. See our <Link to="/instagram-video-dimensions" className="text-orange-600 hover:underline font-medium">Instagram video dimensions guide</Link> if
        you're not sure which ratio to pick.
      </p>

      <h2 className={s.h2}>7. Export and download</h2>
      <p className={s.p}>
        Hit export. Your video renders on our servers (not in your browser) using
        FFmpeg, so the output is a clean, properly encoded MP4 rather than a shaky
        screen recording. When it's done, download it and post it wherever you like —
        there's no watermark and nothing to attribute.
      </p>

      <p className={s.p}>
        Ready to make one? <Link to="/" className="text-orange-600 hover:underline font-medium">Open the editor</Link> and start uploading.
      </p>
    </SeoPageLayout>
  );
}
